import React, { useEffect, useRef, useState } from 'react';
import { cleanRank } from '../../utils/lionAutoLadder';

// Insert one of our listings at a rank the pricer types: that rank and every
// one above it move up one. Closed, a small button (`label`: "#" in the
// listing table, "At rank…" on a group card's approval list); open, a narrow
// text box with a number keypad (not a number box: the wheel would change
// it) whose placeholder gives the range ("1-12"). Enter submits a whole rank
// 1..max (cleanRank) as onSubmit(rank); anything else empties the box (the
// range shows inside the red box), marks it invalid and says so to a screen
// reader (a hidden live hint). Esc closes it and puts focus back on the
// button; leaving the box closes it and focus stays where it went. The table
// row ranks on a click and the group card selects on one, so clicks and keys
// stay in here. A control turned off while open closes. The caller turns the
// rank into the listing shown there (idAtRank).
//
// max: the highest rank (ranked count + 1 = at the end); title: the
// button's tooltip; listingName: "Sec 112 Row A", so the button and the box
// say which listing they act on; buttonClass: the button's look.
const stop = (e) => e.stopPropagation();

function LaRankInsert({
  max, onSubmit, disabled = false, title, label = '#', listingName = '', buttonClass = 'la-link',
}) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [bad, setBad] = useState(false);
  const buttonRef = useRef(null);
  // Set by Esc only: once the box is gone, focus goes back to the button (a
  // close on blur leaves focus where the pricer moved it)
  const refocusRef = useRef(false);
  useEffect(() => {
    if (open || !refocusRef.current) return;
    refocusRef.current = false;
    buttonRef.current?.focus();
  }, [open]);
  const close = () => {
    setOpen(false);
    setText('');
    setBad(false);
  };
  if (disabled && open) close();
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      refocusRef.current = true;
      close();
      return;
    }
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const rank = cleanRank(e.currentTarget.value, max);
    if (rank === null) {
      setText('');
      setBad(true);
      return;
    }
    close();
    onSubmit(rank);
  };
  const what = listingName ? ` ${listingName}` : '';
  const range = `1 to ${max}`;
  return (
    <span className="la-rank-insert" onClick={stop} onKeyDown={stop}>
      {open ? (
        <>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="off"
            className={`form-input form-input-sm la-rank-insert-input${bad ? ' la-input-bad' : ''}`}
            autoFocus
            value={text}
            placeholder={`1-${max}`}
            aria-label={`Rank to insert${what} at, ${range}`}
            aria-invalid={bad || undefined}
            title={bad ? `Not a rank: type a whole number from ${range}` : `Type a rank from ${range}, then Enter (Esc cancels)`}
            onChange={(e) => {
              setText(e.target.value);
              setBad(false);
            }}
            onKeyDown={handleKeyDown}
            onBlur={close}
          />
          <span className="la-rank-insert-hint" aria-live="polite">
            {bad ? `Not a rank: type a whole number from ${range}` : ''}
          </span>
        </>
      ) : (
        <button
          ref={buttonRef}
          type="button"
          className={buttonClass}
          disabled={disabled}
          title={title}
          aria-label={`Insert${what} at rank`}
          onClick={() => setOpen(true)}
        >
          {label}
        </button>
      )}
    </span>
  );
}

export default LaRankInsert;
